import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiChevronDown,
    FiChevronUp,
    FiFileText,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchGstr1Report,

    gstr1B2bSelector,
    gstr1B2cSelector,
    gstr1CreditNotesSelector,
    gstr1SummarySelector,
    gstr1MetaSelector,
    gstr1WarningsSelector,

    gstReportLoadingSelector,
    gstReportErrorSelector,
} from "../../../../../../../store/billing/gst/gstReportSlice";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        toNumber(value)
    );


const formatDate = (
    value
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};


const formatText = (
    value
) => {
    if (
        !value
    ) {
        return "-";
    }

    return String(
        value
    )
        .replace(
            /_/g,
            " "
        )
        .toUpperCase();
};


/*
|--------------------------------------------------------------------------
| RATE BREAKDOWN
|--------------------------------------------------------------------------
*/

const RateBreakdown = ({
    items = [],
}) => {
    if (
        !Array.isArray(
            items
        ) ||
        items.length === 0
    ) {
        return (
            <span className="gstr1-rate-empty">
                No rate breakup
            </span>
        );
    }

    return (
        <div className="gstr1-rate-breakdown">

            {items.map(
                (
                    item,
                    index
                ) => (

                    <div
                        key={
                            `${item?.rate || 0}-${index}`
                        }
                        className="gstr1-rate-row"
                    >

                        <span>
                            {toNumber(
                                item?.rate
                            )}
                            %
                        </span>

                        <strong>
                            {formatCurrency(
                                item?.taxableValue
                            )}
                        </strong>

                    </div>

                )
            )}

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const Gstr1Report = ({
    fromDate,
    toDate,
    financialYear,
}) => {
    const dispatch =
        useDispatch();


    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const b2bRecords =
        useSelector(
            gstr1B2bSelector
        ) || [];

    const b2cRecords =
        useSelector(
            gstr1B2cSelector
        ) || [];

    const creditNotes =
        useSelector(
            gstr1CreditNotesSelector
        ) || [];

    const summary =
        useSelector(
            gstr1SummarySelector
        ) || {};

    const meta =
        useSelector(
            gstr1MetaSelector
        ) || {};

    const warnings =
        useSelector(
            gstr1WarningsSelector
        ) || [];

    const loading =
        useSelector(
            gstReportLoadingSelector
        ) || {};

    const errors =
        useSelector(
            gstReportErrorSelector
        ) || {};


    /*
    |--------------------------------------------------------------------------
    | LOCAL TAB
    |--------------------------------------------------------------------------
    */

    const [
        activeSection,
        setActiveSection,
    ] = useState(
        "B2B"
    );


    /*
    |--------------------------------------------------------------------------
    | EXPANDED RATE ROWS
    |--------------------------------------------------------------------------
    */

    const [
        expandedRows,
        setExpandedRows,
    ] = useState({});


    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadData =
        useCallback(
            async () => {
                await dispatch(
                    fetchGstr1Report({
                        fromDate,
                        toDate,
                        financialYear,
                    })
                );
            },
            [
                dispatch,
                fromDate,
                toDate,
                financialYear,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | SUMMARY DATA
    |--------------------------------------------------------------------------
    */

    const salesSummary =
        summary?.sales ||
        {};

    const creditSummary =
        summary?.creditNotes ||
        {};

    const adjustedSummary =
        summary?.adjusted ||
        {};


    /*
    |--------------------------------------------------------------------------
    | SUMMARY CARDS
    |--------------------------------------------------------------------------
    */

    const cards =
        useMemo(
            () => [
                {
                    label:
                        "Total Invoices",

                    value:
                        toNumber(
                            salesSummary
                                ?.totalInvoices
                        ),

                    help:
                        `${toNumber(
                            salesSummary
                                ?.b2bInvoices
                        )} B2B • ${toNumber(
                            salesSummary
                                ?.b2cInvoices
                        )} B2C`,
                },

                {
                    label:
                        "Taxable Sales",

                    value:
                        formatCurrency(
                            salesSummary
                                ?.taxableAmount
                        ),

                    help:
                        "Before credit-note adjustment",
                },

                {
                    label:
                        "Output GST",

                    value:
                        formatCurrency(
                            salesSummary
                                ?.outputTax
                        ),

                    help:
                        "GST before adjustments",
                },

                {
                    label:
                        "Adjusted Output GST",

                    value:
                        formatCurrency(
                            adjustedSummary
                                ?.outputTax
                        ),

                    help:
                        "After credit-note adjustment",
                },
            ],
            [
                salesSummary,
                adjustedSummary,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | TOGGLE RATE BREAKDOWN
    |--------------------------------------------------------------------------
    */

    const toggleRow =
        (
            key
        ) => {
            setExpandedRows(
                (
                    current
                ) => ({
                    ...current,

                    [key]:
                        !current[
                            key
                        ],
                })
            );
        };


    /*
    |--------------------------------------------------------------------------
    | ACTIVE RECORD COUNT
    |--------------------------------------------------------------------------
    */

    const sectionCount =
        activeSection ===
            "B2B"
            ? b2bRecords.length
            : activeSection ===
                "B2C"
                ? b2cRecords.length
                : creditNotes.length;


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="gstr1-report">

            {/* =====================================================
                ERROR
            ===================================================== */}

            {errors.gstr1 && (

                <div className="gst-dashboard-alert">

                    <FiAlertCircle />

                    <span>
                        {errors.gstr1}
                    </span>

                </div>

            )}


            {/* =====================================================
                WARNINGS
            ===================================================== */}

            {warnings.map(
                (
                    warning,
                    index
                ) => (

                    <div
                        key={
                            index
                        }
                        className="gstr1-warning"
                    >

                        <FiAlertCircle />

                        <span>
                            {warning}
                        </span>

                    </div>

                )
            )}


            {/* =====================================================
                MAIN SUMMARY
            ===================================================== */}

            <section className="gst-dashboard-summary-grid">

                {cards.map(
                    (
                        card
                    ) => (

                        <div
                            key={
                                card.label
                            }
                            className="gst-dashboard-summary-card"
                        >

                            <span>
                                {card.label}
                            </span>

                            <strong>
                                {card.value}
                            </strong>

                            <small>
                                {card.help}
                            </small>

                        </div>

                    )
                )}

            </section>


            {/* =====================================================
                TAX SUMMARY
            ===================================================== */}

            <section className="gst-dashboard-stat-grid gstr1-tax-summary">

                <div>

                    <span>
                        CGST
                    </span>

                    <strong>
                        {formatCurrency(
                            salesSummary
                                ?.cgstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        SGST
                    </span>

                    <strong>
                        {formatCurrency(
                            salesSummary
                                ?.sgstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        IGST
                    </span>

                    <strong>
                        {formatCurrency(
                            salesSummary
                                ?.igstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Cess
                    </span>

                    <strong>
                        {formatCurrency(
                            salesSummary
                                ?.cessAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Credit Notes
                    </span>

                    <strong>
                        {toNumber(
                            creditSummary
                                ?.totalCreditNotes
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Credit Note Value
                    </span>

                    <strong>
                        {formatCurrency(
                            creditSummary
                                ?.noteValue
                        )}
                    </strong>

                </div>

            </section>


            {/* =====================================================
                ADJUSTED SUMMARY
            ===================================================== */}

            <section className="gstr1-adjusted-summary">

                <div>

                    <span>
                        Adjusted Taxable
                    </span>

                    <strong>
                        {formatCurrency(
                            adjustedSummary
                                ?.taxableAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Adjusted CGST
                    </span>

                    <strong>
                        {formatCurrency(
                            adjustedSummary
                                ?.cgstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Adjusted SGST
                    </span>

                    <strong>
                        {formatCurrency(
                            adjustedSummary
                                ?.sgstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Adjusted IGST
                    </span>

                    <strong>
                        {formatCurrency(
                            adjustedSummary
                                ?.igstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Adjusted Invoice Value
                    </span>

                    <strong>
                        {formatCurrency(
                            adjustedSummary
                                ?.invoiceValue
                        )}
                    </strong>

                </div>

            </section>


            {/* =====================================================
                META
            ===================================================== */}

            <section className="gstr1-meta">

                <div>

                    <span>
                        Period
                    </span>

                    <strong>
                        {meta?.fromDate
                            ? formatDate(
                                meta.fromDate
                            )
                            : "-"}
                        {" - "}
                        {meta?.toDate
                            ? formatDate(
                                meta.toDate
                            )
                            : "-"}
                    </strong>

                </div>


                <div>

                    <span>
                        Financial Year
                    </span>

                    <strong>
                        {meta?.financialYear ||
                            "-"}
                    </strong>

                </div>


                <div>

                    <span>
                        Invoice Count
                    </span>

                    <strong>
                        {toNumber(
                            meta?.invoiceCount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Credit Notes
                    </span>

                    <strong>
                        {toNumber(
                            meta?.creditNoteCount
                        )}
                    </strong>

                </div>

            </section>


            {/* =====================================================
                SECTION TABS
            ===================================================== */}

            <section className="gstr1-section-tabs">

                <button
                    type="button"
                    className={
                        activeSection ===
                            "B2B"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setActiveSection(
                            "B2B"
                        )
                    }
                >
                    B2B

                    <span>
                        {b2bRecords.length}
                    </span>
                </button>


                <button
                    type="button"
                    className={
                        activeSection ===
                            "B2C"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setActiveSection(
                            "B2C"
                        )
                    }
                >
                    B2C

                    <span>
                        {b2cRecords.length}
                    </span>
                </button>


                <button
                    type="button"
                    className={
                        activeSection ===
                            "CREDIT_NOTES"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setActiveSection(
                            "CREDIT_NOTES"
                        )
                    }
                >
                    Credit Notes

                    <span>
                        {creditNotes.length}
                    </span>
                </button>


                <div className="gstr1-section-refresh">

                    <span>
                        {sectionCount} records
                    </span>

                    <button
                        type="button"
                        className="gst-dashboard-button"
                        disabled={
                            loading.gstr1
                        }
                        onClick={
                            loadData
                        }
                    >

                        <FiRefreshCw
                            className={
                                loading.gstr1
                                    ? "gst-dashboard-spinner"
                                    : ""
                            }
                        />

                        Refresh

                    </button>

                </div>

            </section>


            {/* =====================================================
                B2B TABLE
            ===================================================== */}

            {activeSection ===
                "B2B" && (

                <section className="gstr1-table-card">

                    {loading.gstr1 &&
                    b2bRecords.length ===
                        0 ? (

                        <div className="gstr1-empty">

                            <FiRefreshCw className="gst-dashboard-spinner" />

                            <strong>
                                Loading B2B invoices...
                            </strong>

                        </div>

                    ) : b2bRecords.length ===
                    0 ? (

                        <div className="gstr1-empty">

                            <FiFileText />

                            <strong>
                                No B2B invoices found
                            </strong>

                        </div>

                    ) : (

                        <div className="gstr1-table-wrapper">

                            <table className="gstr1-table">

                                <thead>

                                    <tr>

                                        <th>
                                            GSTIN
                                        </th>

                                        <th>
                                            Customer
                                        </th>

                                        <th>
                                            Invoice
                                        </th>

                                        <th>
                                            Date
                                        </th>

                                        <th>
                                            POS
                                        </th>

                                        <th>
                                            Supply
                                        </th>

                                        <th className="text-right">
                                            Invoice Value
                                        </th>

                                        <th className="text-right">
                                            Taxable
                                        </th>

                                        <th className="text-right">
                                            CGST
                                        </th>

                                        <th className="text-right">
                                            SGST
                                        </th>

                                        <th className="text-right">
                                            IGST
                                        </th>

                                        <th className="text-right">
                                            Cess
                                        </th>

                                        <th>
                                            RCM
                                        </th>

                                        <th>
                                            Rate
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {b2bRecords.map(
                                        (
                                            item,
                                            index
                                        ) => {
                                            const key =
                                                item?.invoiceId ||
                                                index;

                                            const expanded =
                                                Boolean(
                                                    expandedRows[
                                                        key
                                                    ]
                                                );

                                            return (
                                                <React.Fragment
                                                    key={
                                                        key
                                                    }
                                                >

                                                    <tr>

                                                        <td>
                                                            <strong className="gstr1-gstin">
                                                                {item?.gstin ||
                                                                    "-"}
                                                            </strong>
                                                        </td>

                                                        <td>
                                                            {item?.customerName ||
                                                                "-"}
                                                        </td>

                                                        <td>
                                                            <strong className="gstr1-document">
                                                                {item?.invoiceNumber ||
                                                                    "-"}
                                                            </strong>
                                                        </td>

                                                        <td>
                                                            {formatDate(
                                                                item?.invoiceDate
                                                            )}
                                                        </td>

                                                        <td>
                                                            {item?.placeOfSupply ||
                                                                item?.placeOfSupplyStateCode ||
                                                                "-"}
                                                        </td>

                                                        <td>
                                                            {formatText(
                                                                item?.supplyType
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.invoiceValue
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.taxableAmount
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.cgstAmount
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.sgstAmount
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.igstAmount
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.cessAmount
                                                            )}
                                                        </td>

                                                        <td>
                                                            <span
                                                                className={
                                                                    item?.reverseCharge
                                                                        ? "gstr1-flag gstr1-flag-yes"
                                                                        : "gstr1-flag gstr1-flag-no"
                                                                }
                                                            >
                                                                {item?.reverseCharge
                                                                    ? "YES"
                                                                    : "NO"}
                                                            </span>
                                                        </td>

                                                        <td>

                                                            <button
                                                                type="button"
                                                                className="gstr1-rate-toggle"
                                                                onClick={() =>
                                                                    toggleRow(
                                                                        key
                                                                    )
                                                                }
                                                            >

                                                                {expanded
                                                                    ? <FiChevronUp />
                                                                    : <FiChevronDown />}

                                                                Rates

                                                            </button>

                                                        </td>

                                                    </tr>


                                                    {expanded && (

                                                        <tr className="gstr1-rate-detail-row">

                                                            <td
                                                                colSpan={14}
                                                            >

                                                                <RateBreakdown
                                                                    items={
                                                                        item?.rateBreakdown ||
                                                                        []
                                                                    }
                                                                />

                                                            </td>

                                                        </tr>

                                                    )}

                                                </React.Fragment>
                                            );
                                        }
                                    )}

                                </tbody>

                            </table>

                        </div>

                    )}

                </section>

            )}


            {/* =====================================================
                B2C TABLE
            ===================================================== */}

            {activeSection ===
                "B2C" && (

                <section className="gstr1-table-card">

                    {loading.gstr1 &&
                    b2cRecords.length ===
                        0 ? (

                        <div className="gstr1-empty">

                            <FiRefreshCw className="gst-dashboard-spinner" />

                            <strong>
                                Loading B2C invoices...
                            </strong>

                        </div>

                    ) : b2cRecords.length ===
                    0 ? (

                        <div className="gstr1-empty">

                            <FiFileText />

                            <strong>
                                No B2C invoices found
                            </strong>

                        </div>

                    ) : (

                        <div className="gstr1-table-wrapper">

                            <table className="gstr1-table">

                                <thead>

                                    <tr>

                                        <th>
                                            Customer
                                        </th>

                                        <th>
                                            Invoice
                                        </th>

                                        <th>
                                            Date
                                        </th>

                                        <th>
                                            POS
                                        </th>

                                        <th>
                                            Supply
                                        </th>

                                        <th className="text-right">
                                            Invoice Value
                                        </th>

                                        <th className="text-right">
                                            Taxable
                                        </th>

                                        <th className="text-right">
                                            CGST
                                        </th>

                                        <th className="text-right">
                                            SGST
                                        </th>

                                        <th className="text-right">
                                            IGST
                                        </th>

                                        <th className="text-right">
                                            Cess
                                        </th>

                                        <th>
                                            Rate
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {b2cRecords.map(
                                        (
                                            item,
                                            index
                                        ) => {
                                            const key =
                                                item?.invoiceId ||
                                                index;

                                            const expanded =
                                                Boolean(
                                                    expandedRows[
                                                        `b2c-${key}`
                                                    ]
                                                );

                                            return (
                                                <React.Fragment
                                                    key={
                                                        key
                                                    }
                                                >

                                                    <tr>

                                                        <td>
                                                            {item?.customerName ||
                                                                "B2C Customer"}
                                                        </td>

                                                        <td>
                                                            <strong className="gstr1-document">
                                                                {item?.invoiceNumber ||
                                                                    "-"}
                                                            </strong>
                                                        </td>

                                                        <td>
                                                            {formatDate(
                                                                item?.invoiceDate
                                                            )}
                                                        </td>

                                                        <td>
                                                            {item?.placeOfSupply ||
                                                                item?.placeOfSupplyStateCode ||
                                                                "-"}
                                                        </td>

                                                        <td>
                                                            {formatText(
                                                                item?.supplyType
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.invoiceValue
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.taxableAmount
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.cgstAmount
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.sgstAmount
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.igstAmount
                                                            )}
                                                        </td>

                                                        <td className="text-right">
                                                            {formatCurrency(
                                                                item?.cessAmount
                                                            )}
                                                        </td>

                                                        <td>

                                                            <button
                                                                type="button"
                                                                className="gstr1-rate-toggle"
                                                                onClick={() =>
                                                                    toggleRow(
                                                                        `b2c-${key}`
                                                                    )
                                                                }
                                                            >

                                                                {expanded
                                                                    ? <FiChevronUp />
                                                                    : <FiChevronDown />}

                                                                Rates

                                                            </button>

                                                        </td>

                                                    </tr>


                                                    {expanded && (

                                                        <tr className="gstr1-rate-detail-row">

                                                            <td
                                                                colSpan={12}
                                                            >

                                                                <RateBreakdown
                                                                    items={
                                                                        item?.rateBreakdown ||
                                                                        []
                                                                    }
                                                                />

                                                            </td>

                                                        </tr>

                                                    )}

                                                </React.Fragment>
                                            );
                                        }
                                    )}

                                </tbody>

                            </table>

                        </div>

                    )}

                </section>

            )}


            {/* =====================================================
                CREDIT NOTES TABLE
            ===================================================== */}

            {activeSection ===
                "CREDIT_NOTES" && (

                <section className="gstr1-table-card">

                    {loading.gstr1 &&
                    creditNotes.length ===
                        0 ? (

                        <div className="gstr1-empty">

                            <FiRefreshCw className="gst-dashboard-spinner" />

                            <strong>
                                Loading credit notes...
                            </strong>

                        </div>

                    ) : creditNotes.length ===
                    0 ? (

                        <div className="gstr1-empty">

                            <FiFileText />

                            <strong>
                                No GST credit notes found
                            </strong>

                        </div>

                    ) : (

                        <div className="gstr1-table-wrapper">

                            <table className="gstr1-table">

                                <thead>

                                    <tr>

                                        <th>
                                            Credit Note
                                        </th>

                                        <th>
                                            Date
                                        </th>

                                        <th>
                                            Original Invoice
                                        </th>

                                        <th>
                                            Customer
                                        </th>

                                        <th>
                                            GSTIN
                                        </th>

                                        <th className="text-right">
                                            Taxable
                                        </th>

                                        <th className="text-right">
                                            CGST
                                        </th>

                                        <th className="text-right">
                                            SGST
                                        </th>

                                        <th className="text-right">
                                            IGST
                                        </th>

                                        <th className="text-right">
                                            Cess
                                        </th>

                                        <th className="text-right">
                                            Note Value
                                        </th>

                                        <th>
                                            Status
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {creditNotes.map(
                                        (
                                            item,
                                            index
                                        ) => (

                                            <tr
                                                key={
                                                    item?._id ||
                                                    index
                                                }
                                            >

                                                <td>
                                                    <strong className="gstr1-document">
                                                        {item?.noteNumber ||
                                                            "-"}
                                                    </strong>
                                                </td>

                                                <td>
                                                    {formatDate(
                                                        item?.noteDate
                                                    )}
                                                </td>

                                                <td>
                                                    {item?.originalInvoiceNumber ||
                                                        "-"}
                                                </td>

                                                <td>
                                                    {item?.customerName ||
                                                        "-"}
                                                </td>

                                                <td>
                                                    <strong className="gstr1-gstin">
                                                        {item?.customerGstin ||
                                                            "-"}
                                                    </strong>
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item?.taxableAmount
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item?.cgstAmount
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item?.sgstAmount
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item?.igstAmount
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item?.cessAmount
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    <strong>
                                                        {formatCurrency(
                                                            item?.noteValue
                                                        )}
                                                    </strong>
                                                </td>

                                                <td>
                                                    {item?.status ||
                                                        "-"}
                                                </td>

                                            </tr>

                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>

                    )}

                </section>

            )}

        </div>
    );
};


export default Gstr1Report;